import { Injectable } from '@angular/core';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import {
  DisplayByMode,
  selectNowDeliveryPeriod,
  selectProductIsLocked,
} from '@epicuro-next/state/config-strip-state';
import { selectPresetValue } from '@epicuro-next/state/p-basket-state';
import { arrayIsNotEmpty, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RouterNavigatedAction, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { OrderType } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, merge, of } from 'rxjs';
import {
  delay,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  addAutoDeliveryPeriod,
  addAutoDeliveryPeriodSuccess,
  addDeliveryPeriod,
  copyFromCacheDeliveryPeriods,
  deleteBulkDeliveryPeriod,
  deleteDeliveryPeriod,
  executeDeliveryPeriodSelectionAction,
  futureDeliveryPeriodsComponentInitialized,
  initCachedDeliveryPeriods,
  initCalculationFutureDeliveryPeriodsFromInventories,
  loadProductSuccess,
  openFutureDatepicker,
  preSelectAnyDeliveryPeriod,
  preSelectFutureDeliveryPeriod,
  preSelectNowDeliveryPeriod,
  selectBulkDeliveryPeriod,
  selectFutureDeliveryPeriod,
  setActiveDeliveryShop,
  setDisplayByMode,
  setPreselectedDeliveryPeriod,
} from '../actions';
import {
  isMonthType,
  selectAvailableInventories,
  selectAvailableVariants,
  selectBulkDeliveryPeriods,
  selectCachedDeliveryPeriods,
  selectCachedSelectedDeliveryPeriod,
  selectConfigStripInStoreDate,
  selectDeliveryPeriods,
  selectDeliveryShops,
  selectDeliveryWindowType,
  selectFutureDeliveryPeriods,
  selectGlobalDeliveryPeriodsRange,
  selectGlobalFutureDeliveryMonths,
  selectHasAvailableFutureInventories,
  selectHasNowInventories,
  selectIsConfigStripOpen,
  selectIsNotAbleSelectAnyPeriods,
  selectPreselectedDeliveryPeriod,
  selectPreSelectedDeliveryPeriodType,
  selectPreSelectedOrderType,
  selectProduct,
  selectShouldSelectBulk,
  selectShouldSelectFuture,
} from '../selectors';
import { selectConfigStripIsInStoreDateEnabled } from '../selectors/config-strip-configuration.selectors';
import { checkIsNow } from '../shared/check-is-now';
import { formatDisplayByMode } from '../shared/shared';

@Injectable()
export class DeliveryPeriodsEffects {
  public copyFutureDeliveryPeriodsFromStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initCachedDeliveryPeriods),
      withLatestFrom(
        this.store.pipe(select(selectCachedDeliveryPeriods)),
        this.store.pipe(select(selectCachedSelectedDeliveryPeriod)),
        this.store.pipe(
          select(selectGlobalDeliveryPeriodsRange),
          filter(isNonNullish),
        ),
        this.store.select(selectDeliveryShops),
      ),
      map(
        ([
          ,
          selectedDeliveryPeriods,
          lastSelectedDate,
          availableDateRange,
          deliveryShops,
        ]) =>
          copyFromCacheDeliveryPeriods({
            cache: { selectedDeliveryPeriods, lastSelectedDate },
            availableDateRange,
            deliveryShops,
          }),
      ),
    ),
  );

  // it takes "preSelected" fields from cs store and distributes for all shops
  public usePreselectedDeliveryForAllShops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copyFromCacheDeliveryPeriods),
      withLatestFrom(
        this.store.select(selectDeliveryShops),
        this.store.select(selectDeliveryPeriods),
        this.store.select(selectPreSelectedDeliveryPeriodType),
        this.store.select(selectPreselectedDeliveryPeriod),
        this.store.select(selectPreSelectedOrderType),
      ),
      map(
        ([
          _,
          deliveryShops,
          deliveryPeriods,
          preSelectedDeliveryPeriodType,
          preSelectedDeliveryPeriod,
          preSelectedOrderType,
        ]) =>
          setPreselectedDeliveryPeriod({
            deliveryPeriods,
            deliveryShops,
            preSelectedDeliveryPeriodType,
            preSelectedDeliveryPeriod,
            preSelectedOrderType,
          }),
      ),
    ),
  );

  public calculateFutureDeliveryPeriodsFromInventories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initCalculationFutureDeliveryPeriodsFromInventories),
      switchMap(() =>
        combineLatest([
          this.store.pipe(
            select(selectDeliveryWindowType),
            filter(isNonNullish),
          ),
          this.store.pipe(
            select(selectGlobalFutureDeliveryMonths),
            filter(isNonNullish),
          ),
        ]).pipe(
          map(([windowType, futureMonths]) =>
            isMonthType(windowType) ? futureMonths : null,
          ),
          filter(isNonNullish),
          take(1),
        ),
      ),
      withLatestFrom(this.store.select(selectDeliveryShops)),
      switchMap(([months, deliveryShops]) => [
        ...months.map((from) =>
          addAutoDeliveryPeriod({
            deliveryPeriod: { from },
            deliveryShops: deliveryShops ?? [],
          }),
        ),
        addAutoDeliveryPeriodSuccess(),
      ]),
    ),
  );

  public preselectDeliveryPeriodAfterAutoAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addAutoDeliveryPeriodSuccess),
      withLatestFrom(
        this.store.select(selectDeliveryShops),
        this.store.select(selectDeliveryPeriods),
        this.store.select(selectPreSelectedDeliveryPeriodType),
        this.store.select(selectPreselectedDeliveryPeriod),
        this.store.select(selectPreSelectedOrderType),
      ),
      switchMap(
        ([
          _,
          deliveryShops,
          deliveryPeriods,
          preSelectedDeliveryPeriodType,
          preSelectedDeliveryPeriod,
          preSelectedOrderType,
        ]) => [
          setPreselectedDeliveryPeriod({
            deliveryPeriods,
            deliveryShops,
            preSelectedDeliveryPeriodType,
            preSelectedDeliveryPeriod,
            preSelectedOrderType,
          }),
        ],
      ),
    ),
  );

  public executeDeliveryPeriodSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProductSuccess),
      map(() => executeDeliveryPeriodSelectionAction()),
    ),
  );

  private readonly articleNonEmptyInventories$ = this.store.pipe(
    select(selectAvailableInventories(true)),
    filter((v) => isNonNullish(v) && arrayIsNotEmpty(v)),
    take(1),
  );

  public setDeliveryWindowType$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter((action: RouterNavigatedAction) =>
          action.payload.routerState.url.includes(
            `${APP_TOP_LEVEL_PATH.cs.outlet}:${APP_TOP_LEVEL_PATH.cs.path}`,
          ),
        ),
        withLatestFrom(this.store.select(selectIsConfigStripOpen)),
        map(([, isOpen]) => isOpen),
        filter((isOpen) => !isOpen),
      ),
      this.actions$.pipe(ofType(executeDeliveryPeriodSelectionAction)),
    ).pipe(
      switchMap(() =>
        this.store.pipe(select(selectProduct), filter(isNonNullish), take(1)),
      ),
      switchMap(() => this.articleNonEmptyInventories$),
      withLatestFrom(
        this.store.select(selectPresetValue),
        this.store.select(selectHasNowInventories),
        this.store.select(selectHasAvailableFutureInventories),
        this.store.select(selectPreselectedDeliveryPeriod),
        this.store.select(selectDeliveryWindowType),
      ),
      switchMap(
        ([
          ,
          preset,
          hasNowInventories,
          hasFutureInventories,
          preselectedDeliveryPeriod,
          windowType,
        ]) => {
          if (preselectedDeliveryPeriod) {
            // preselect now if preselected date is actually NOW date
            if (
              hasNowInventories &&
              checkIsNow(windowType, preselectedDeliveryPeriod as string)
            ) {
              return of(preSelectNowDeliveryPeriod());
            }

            // no need to preselect if we've already done this
            return EMPTY;
          }

          if (
            preset &&
            preset === OrderType.Future.toLowerCase() &&
            hasFutureInventories
          ) {
            return of(
              preSelectFutureDeliveryPeriod({
                from: null,
              }),
            );
          }

          // if product does not have now inventories, we will not show now delivery period to select
          // so it does not make sense to preselect now
          if (hasNowInventories) {
            return of(preSelectNowDeliveryPeriod());
          }

          // we are not determined what to select in this situation
          // so we ask reducers to select at least something
          return of(preSelectAnyDeliveryPeriod());
        },
      ),
    ),
  );

  public preselectDeliveryPeriodsOnLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        loadProductSuccess,
        preSelectFutureDeliveryPeriod,
        preSelectNowDeliveryPeriod,
        preSelectAnyDeliveryPeriod,
      ),
      withLatestFrom(
        this.store.select(selectDeliveryShops),
        this.store.select(selectDeliveryPeriods),
        this.store.select(selectPreSelectedDeliveryPeriodType),
        this.store.select(selectPreselectedDeliveryPeriod),
        this.store.select(selectPreSelectedOrderType),
      ),
      map(
        ([
          _,
          deliveryShops,
          deliveryPeriods,
          preSelectedDeliveryPeriodType,
          preSelectedDeliveryPeriod,
          preSelectedOrderType,
        ]) =>
          setPreselectedDeliveryPeriod({
            deliveryPeriods,
            deliveryShops,
            preSelectedDeliveryPeriod,
            preSelectedDeliveryPeriodType,
            preSelectedOrderType,
          }),
      ),
    ),
  );

  public preselectISDPeriod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProductSuccess),
      concatLatestFrom(() => [
        this.store.select(selectConfigStripIsInStoreDateEnabled),
        this.store.select(selectPresetValue),
      ]),
      filter(
        ([_, isRuleEnabled, preset]) =>
          Boolean(isRuleEnabled) && preset === 'future',
      ),
      concatLatestFrom(() => [
        this.store.select(selectDeliveryShops),
        this.store
          .select(selectConfigStripInStoreDate)
          .pipe(filter(isNonNullish)),
      ]),
      switchMap(([_, deliveryShops, from]) => [
        addDeliveryPeriod({
          deliveryPeriod: { from, isTrusted: true },
          deliveryShops: deliveryShops ?? [],
        }),
        selectFutureDeliveryPeriod({ deliveryPeriod: { from } }),
      ]),
    ),
  );

  // * * *
  // 3 glorious effects to fix navigation in CS delivery periods:
  //
  // select future on delete bulk period when no bulk left or preselect reducer could not select requested date,
  // select bulk on delete future period when no future left or preselect reducer could not select requested date,
  // open datepicker on open shop tab or delete any period when there are no periods to select
  // * * *

  // fallback to automatically select future period
  public selectFutureWhenNoBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPreselectedDeliveryPeriod, deleteBulkDeliveryPeriod),
      switchMap(() => this.articleNonEmptyInventories$),
      withLatestFrom(this.store.pipe(select(selectShouldSelectFuture))),
      filter(([, shouldSelectFuture]) => shouldSelectFuture),
      switchMap(() =>
        this.store.pipe(
          select(selectFutureDeliveryPeriods),
          filter(arrayIsNotEmpty),
          take(1),
          map((periods) =>
            selectFutureDeliveryPeriod({
              deliveryPeriod: { from: periods[0] },
            }),
          ),
        ),
      ),
    ),
  );

  // fallback to automatically select bulk period
  public selectBulkWhenNoFuture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPreselectedDeliveryPeriod, deleteDeliveryPeriod),
      switchMap(() => this.articleNonEmptyInventories$),
      withLatestFrom(this.store.pipe(select(selectShouldSelectBulk))),
      filter(([, shouldSelectBulk]) => shouldSelectBulk),
      switchMap(() =>
        this.store.pipe(
          select(selectBulkDeliveryPeriods),
          filter(arrayIsNotEmpty),
          take(1),
          map((periods) => selectBulkDeliveryPeriod(periods[0])),
        ),
      ),
    ),
  );

  // if none of 2 effects above did work or we open shop tab and have nothing to select
  // we check if it is time to open datepicker
  public openDatepickerWhenNothingToSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        futureDeliveryPeriodsComponentInitialized,
        setActiveDeliveryShop,
        deleteBulkDeliveryPeriod,
        deleteDeliveryPeriod,
        setPreselectedDeliveryPeriod,
      ),
      switchMap(() => this.articleNonEmptyInventories$),
      delay(0), // because state has to be updated first
      withLatestFrom(this.store.pipe(select(selectIsNotAbleSelectAnyPeriods))),
      filter(([, nothingToSelect]) => nothingToSelect),
      map(() => openFutureDatepicker()),
    ),
  );

  public resetDisplayByModeOnDeliveryPeriodSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectFutureDeliveryPeriod, selectNowDeliveryPeriod),
      concatLatestFrom(() => [
        this.store.pipe(select(selectProductIsLocked)),
        this.store.pipe(
          select(selectAvailableVariants),
          map((variants) => !!variants?.length),
        ),
      ]),
      map(([_, locked, hasLooseSizes]) =>
        setDisplayByMode({
          displayByMode: formatDisplayByMode(
            hasLooseSizes ? DisplayByMode.Size : DisplayByMode.Prepack,
            locked,
          ),
        }),
      ),
    ),
  );

  constructor(private actions$: Actions, private store: Store) {}
}
